import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import React, { useMemo, useState } from 'react';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import TopEntityChartV2Loading from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2Loading';
import useAtlasV2AdvancedSearchData from 'src/serverProxy/useAtlasV2AdvancedSearchData';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useAppSelector } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { safeDivide } from 'src/utils/app';

const fieldOptions = [
  {
    label: 'Market Share of Retail Sales',
    id: 'retailSales',
    fieldName: 'retailSales',
    marketMetric: 'retailSales',
    marketShareKey: ['retailSales_sum_value', 'retailSales_sum_value']
  },
  {
    label: '1P Market Share of Retail Sales',
    id: 'onePSales/retailSales',
    fieldName: 'onePSales',
    marketMetric: 'retailSales',
    marketShareKey: ['onePSales_sum_value', 'retailSales_sum_value']
  },
  {
    label: '1P Market Share of 1P Retail Sales',
    id: 'onePSales/onePRetailSales',
    fieldName: 'onePSales',
    marketMetric: 'onePSales',
    marketShareKey: ['onePSales_sum_value', 'onePSales_sum_value']
  },
  {
    label: '3P Market Share of Retail Sales',
    id: 'threePSales',
    fieldName: 'threePSales',
    marketMetric: 'retailSales',
    marketShareKey: ['threePSales_sum_value', 'retailSales_sum_value']
  },
  {
    label: '3P Market Share of 3P Retail Sales',
    id: 'threePSales/threePSales',
    fieldName: 'threePSales',
    marketMetric: 'threePSales',
    marketShareKey: ['threePSales_sum_value', 'threePSales_sum_value']
  }
];

const groupByOptions = [
  { id: 'brandId', label: 'Brand', dataParseKey: 'brand' },
  { id: 'parentBrandId', label: 'Manufacturer', dataParseKey: 'parentBrand' }
];

/**
 *
 * Sales Summary double dropdown
 */
const AtlasV2DoubleDropdownCategoryMarketShareChart = () => {
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const legendComparisonLabel = useAppSelector((state) => state.comparisonTimePeriod.displayName);

  const [selectedMainMetricField, setSelectedMainMetricField] = useState('retailSales');

  const currentMainMetricOption =
    fieldOptions.find((option) => option.id === selectedMainMetricField) || fieldOptions[0];

  const [selectedGroupByFieldId, setSelectedGroupByFieldId] = useState('brandId');
  const currentGroupByOption =
    groupByOptions.find((option) => option.id === selectedGroupByFieldId) || groupByOptions[0];

  const { data: marketShareData, isLoading: isMarketShareLoading } = useAtlasV2AdvancedSearchData({
    fields: [{ name: currentMainMetricOption.marketMetric }],
    groupByField: 'retailerId',
    indexName: 'sales',
    requestId: `Atlas-${selectedMainMetricField}- market`,
    shouldFetchComparison: true
  });

  const { data: targetData, isLoading: isTargetLoading } = useAtlasV2AdvancedSearchData({
    fields: [{ name: currentMainMetricOption.fieldName }],
    groupByField: selectedGroupByFieldId,
    indexName: 'sales',
    requestId: `Atlas-${selectedMainMetricField}- target`,
    shouldFetchComparison: true
  });

  const parsedData = useMemo(() => {
    const comparisonData = [];
    const currentData = [];
    const categoryData = [];

    if (!isMarketShareLoading && !isTargetLoading) {
      const currentMarketShare = marketShareData?.[0]?.values?.[currentMainMetricOption?.marketShareKey?.[1]];
      const compareMarketShare = marketShareData?.[0]?.comparisonValues?.[currentMainMetricOption?.marketShareKey?.[1]];

      targetData.forEach((datum) => {
        const categoryObj =
          datum?.metadata?.[currentGroupByOption.dataParseKey] ??
          datum?.comparisonMetadata?.[currentGroupByOption.dataParseKey];
        const formattedCategoryObj = {
          name: categoryObj?.name,
          id: categoryObj?.id,
          type: currentGroupByOption?.dataParseKey?.toLocaleLowerCase()
        };

        const responseKey = currentMainMetricOption.marketShareKey[0];
        const currentDatum = datum?.values?.[responseKey] ?? 0;
        const comparisonDatum = datum?.comparisonValues?.[responseKey] ?? 0;

        if (!categoryObj || !categoryObj.name) {
          return;
        }

        comparisonData.push([categoryObj.name, safeDivide(comparisonDatum, compareMarketShare)]);
        currentData.push([categoryObj.name, safeDivide(currentDatum, currentMarketShare)]);
        categoryData.push(formattedCategoryObj);
      });
    }

    return { currentData, categoryData, comparisonData };
  }, [
    currentGroupByOption,
    currentMainMetricOption,
    isMarketShareLoading,
    isTargetLoading,
    marketShareData,
    targetData
  ]);

  return (
    <BeaconChartWithLegend
      convertSeriesToDelimitedData={async () => {
        const label = `Category Market Share by ${currentGroupByOption.label}`;

        return saveBarChartAsDelimitedData(parsedData.currentData, parsedData.comparisonData, {
          fileTitle: `Retail Sales - ${label}`,
          metricLabel: label
        });
      }}
      includeSummaryInfo
      primaryLegendProps={{
        displayName: legendLabel
      }}
      comparisonLegendProps={{
        displayName: legendComparisonLabel
      }}
      loading={isMarketShareLoading || isTargetLoading}
      loadingComponent={<TopEntityChartV2Loading enableSubTitleSkeleton />}
      title={
        <Flex alignItems="center">
          <SlDropdownMenu
            buttonVariant="title"
            options={fieldOptions}
            selectedId={selectedMainMetricField}
            onChange={(option) => setSelectedMainMetricField(option.id)}
            defaultLabel="Market Share of Retail Sales"
          />
          <Text variant="h4" sx={{ margin: '0 8px' }}>
            by
          </Text>
          <SlDropdownMenu
            buttonVariant="title"
            options={groupByOptions}
            selectedId={selectedGroupByFieldId}
            onChange={(option) => setSelectedGroupByFieldId(option.id)}
            defaultLabel="Brand"
          />
        </Flex>
      }
    >
      <TopEntityChartV2
        chartSeries={[
          {
            type: 'column',
            data: parsedData.comparisonData,
            categories: parsedData.categoryData,
            metricType: METRICTYPE.PERCENT
          },
          {
            type: 'column',
            data: parsedData.currentData,
            categories: parsedData.categoryData,
            metricType: METRICTYPE.PERCENT
          }
        ]}
      />
    </BeaconChartWithLegend>
  );
};

export default AtlasV2DoubleDropdownCategoryMarketShareChart;
